import * as Sentry from '@sentry/react-native';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useCompareCap } from '../hooks/useCompareCap';
import { useListingsFeed } from '../hooks/useListingsFeed';
import { computeBadgeCohorts } from '../utils/listingBadges';
import { apiService } from '../services/apiService';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';
import { useAuth } from './AuthContext';
import { useTier } from './TierContext';

/**
 * ListingsFeedContext - Manages listing feed state for a vehicle model.
 *
 * Provides centralized state management for the listings feed including:
 * - Current model's listings (via useListingsFeed hook)
 * - Save/dismiss interaction handlers
 * - Comparison selection state
 * - Loading and error states
 *
 * Follows the same pattern as ProductDetailContext.
 *
 * @context ListingsFeedContext
 */

// Create the context with a default shape
export const ListingsFeedContext = createContext({
  // Feed data
  listings: [],
  noLongerAvailableListings: [],
  isLoading: false,
  isEmpty: false,
  feedGeneratedAt: null,

  // Interaction states
  savingIds: new Set(),
  dismissingIds: new Set(),

  // Comparison state
  comparisonSelection: [],

  // Error state
  error: null,

  // Methods
  refetch: () => {},
  handleSave: () => {},
  handleUnsave: () => {},
  handleDismiss: () => {},
  handleView: () => {},
  addToComparison: () => {},
  removeFromComparison: () => {},
  clearComparison: () => {},
});

/**
 * ListingsFeedProvider - Provider component for listing feed data.
 *
 * @param {Object} props
 * @param {string} props.modelId - The vehicle model ID to fetch listings for
 * @param {React.ReactNode} props.children - Child components
 */
export const ListingsFeedProvider = ({ modelId, children }) => {
  const { session } = useAuth();
  const posthog = usePostHog();
  const { hasProAccess, triggerUpgradeModal } = useTier();

  // Feed data from the useListingsFeed hook
  const { listings, noLongerAvailableListings, isLoading, isEmpty, refetch, feedGeneratedAt, updateListing } = useListingsFeed(
    modelId,
    session
  );

  // Interaction loading states (track which listings have in-flight requests)
  const [savingIds, setSavingIds] = useState(new Set());
  const [dismissingIds, setDismissingIds] = useState(new Set());

  // Comparison selection (max 2 listings for side-by-side comparison)
  const [comparisonSelection, setComparisonSelection] = useState([]);

  // Error state for interactions
  const [error, setError] = useState(null);

  // Step 3 Free cap: saved listings. Kept in a ref so handleSave can do a
  // synchronous cap check without waiting on a state update round-trip (avoids
  // a UX flash where the heart fills before the paywall pops).
  const savedCountRef = useRef(0);
  const userId = session?.user?.id;

  // Load initial saved-listing count for the user (cross-model, lifetime).
  useEffect(() => {
    if (!userId) {
      savedCountRef.current = 0;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { count, error: countError } = await supabase
          .from('user_listing_feeds')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .not('saved_at', 'is', null);
        if (cancelled) return;
        if (countError) {
          logger.error('[ListingsFeedContext] Failed to load saved count:', countError);
          return;
        }
        savedCountRef.current = count || 0;
      } catch (err) {
        if (!cancelled) {
          logger.error('[ListingsFeedContext] saved-count query threw:', err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Step 3 Free cap: comparison selection. Ref mirrors state so the cap
  // branch in addToComparison can read the latest selection synchronously
  // (setState functional updater would fire events inside a reducer, which
  // is fragile for PostHog + modal dispatch).
  const comparisonSelectionRef = useRef([]);
  useEffect(() => {
    comparisonSelectionRef.current = comparisonSelection;
  }, [comparisonSelection]);

  /**
   * Record a listing interaction via the edge function.
   * Handles optimistic UI updates via the loading ID sets.
   *
   * @param {string} listingId - The feed entry ID
   * @param {string} actionType - The interaction type
   * @param {Object} [metadata] - Optional metadata
   */
  const recordInteraction = useCallback(
    async (listingId, actionType, metadata) => {
      try {
        await apiService.recordListingInteraction(listingId, actionType, metadata);
        return true;
      } catch (err) {
        logger.error(
          `[ListingsFeedContext] Error recording ${actionType} interaction:`,
          err
        );
        Sentry.captureException(err, {
          tags: {
            context: 'ListingsFeedContext',
            operation: 'recordInteraction',
            actionType,
          },
          contexts: {
            interaction: { listing_id: listingId, action_type: actionType },
          },
        });
        setError(`Failed to ${actionType} listing. Please try again.`);
        return false;
      }
    },
    []
  );

  /**
   * Save a listing (heart icon). Optimistic — toggles saved_at locally, then
   * fires the API call in the background. No refetch needed.
   * @param {string} listingId - The feed entry ID
   */
  const handleSave = useCallback(
    async (listingId) => {
      setError(null);

      // Step 3 Free save cap: 10 lifetime saves. Checked synchronously from
      // ref so the heart never flashes on before the paywall appears.
      if (!hasProAccess && savedCountRef.current >= 10) {
        posthog?.capture('saved_listings_cap_hit', {
          current_count: savedCountRef.current,
        });
        triggerUpgradeModal({ source: 'cap_saved_listings' });
        return;
      }

      // Optimistically set saved_at
      updateListing(listingId, (l) => ({ ...l, saved_at: new Date().toISOString() }));

      const entry = listings.find((l) => l.id === listingId);
      posthog?.capture('listing_saved', {
        listing_id: listingId,
        score: entry?.score ?? null,
        model_id: entry?.model_id ?? modelId,
      });

      const success = await recordInteraction(listingId, 'save');
      if (success) {
        savedCountRef.current += 1;
      } else {
        // Revert on failure
        updateListing(listingId, (l) => ({ ...l, saved_at: null }));
      }
    },
    [recordInteraction, updateListing, listings, posthog, modelId, hasProAccess, triggerUpgradeModal]
  );

  /**
   * Unsave a previously saved listing. Optimistic — clears saved_at locally.
   * @param {string} listingId - The feed entry ID
   */
  const handleUnsave = useCallback(
    async (listingId) => {
      setError(null);

      // Capture previous value for rollback
      const entry = listings.find((l) => l.id === listingId);
      const prevSavedAt = entry?.saved_at;

      // Optimistically clear saved_at
      updateListing(listingId, (l) => ({ ...l, saved_at: null }));

      const success = await recordInteraction(listingId, 'save');
      if (success) {
        // Mirror the server: decrement lifetime saved count on unsave.
        savedCountRef.current = Math.max(0, savedCountRef.current - 1);
      } else {
        // Revert on failure
        updateListing(listingId, (l) => ({ ...l, saved_at: prevSavedAt }));
      }
    },
    [recordInteraction, updateListing, listings]
  );

  /**
   * Dismiss a listing (X button).
   * The caller should handle the undo toast UI — only call this after the undo window expires.
   * @param {string} listingId - The feed entry ID
   */
  const handleDismiss = useCallback(
    async (listingId) => {
      setDismissingIds((prev) => new Set(prev).add(listingId));
      setError(null);

      const entry = listings.find((l) => l.id === listingId);

      const success = await recordInteraction(listingId, 'dismiss');

      setDismissingIds((prev) => {
        const next = new Set(prev);
        next.delete(listingId);
        return next;
      });

      if (success) {
        posthog?.capture('listing_dismissed', {
          listing_id: listingId,
          score: entry?.score ?? null,
          model_id: entry?.model_id ?? modelId,
        });
        // Refetch to remove dismissed listing from the feed
        refetch();
      }
    },
    [recordInteraction, refetch, listings, posthog, modelId]
  );

  /**
   * Record a view interaction when a listing popup opens.
   * @param {string} listingId - The feed entry ID
   */
  const handleView = useCallback(
    async (listingId) => {
      setError(null);
      // Fire-and-forget — don't block the UI for view tracking
      recordInteraction(listingId, 'view');
    },
    [recordInteraction]
  );

  /**
   * Remove a listing from the comparison selection.
   * @param {string} listingId - The feed entry ID to remove
   */
  const removeFromComparison = useCallback((listingId) => {
    setComparisonSelection((prev) => prev.filter((l) => l.id !== listingId));
  }, []);

  /**
   * Clear the comparison selection.
   */
  const clearComparison = useCallback(() => {
    setComparisonSelection([]);
  }, []);

  // Step 3: Free compare cap policy lives in a shared hook so that
  // `UnifiedFeedScreen` (which does not mount `ListingsFeedProvider`) can
  // enforce the same cap. State ownership stays here; the hook only dispatches.
  const { attemptCompareSelect } = useCompareCap();

  /**
   * Add a listing to the comparison selection (max 2).
   * Free users: 1st selection allowed, 2nd triggers upgrade modal and
   * dismissing the modal clears the selection.
   * @param {Object} listing - The listing object to compare
   */
  const addToComparison = useCallback(
    (listing) => {
      const current = comparisonSelectionRef.current;
      attemptCompareSelect(listing, current, {
        onSelect: (next) => setComparisonSelection(next),
        onClear: () => clearComparison(),
      });
    },
    [attemptCompareSelect, clearComparison]
  );

  // Cohort sets for image badges (TOP MATCH / BEST DEAL). Same helper as
  // UnifiedFeedScreen so the rules stay in one place.
  const { topMatchIds, bestPriceIds } = useMemo(() => computeBadgeCohorts(listings), [listings]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      // Feed data
      listings,
      noLongerAvailableListings,
      isLoading,
      isEmpty,
      feedGeneratedAt,

      // Badge cohort sets
      topMatchIds,
      bestPriceIds,

      // Interaction states
      savingIds,
      dismissingIds,

      // Comparison state
      comparisonSelection,

      // Error state
      error,

      // Methods
      refetch,
      handleSave,
      handleUnsave,
      handleDismiss,
      handleView,
      addToComparison,
      removeFromComparison,
      clearComparison,
    }),
    [
      listings,
      noLongerAvailableListings,
      isLoading,
      isEmpty,
      feedGeneratedAt,
      topMatchIds,
      bestPriceIds,
      savingIds,
      dismissingIds,
      comparisonSelection,
      error,
      refetch,
      handleSave,
      handleUnsave,
      handleDismiss,
      handleView,
      addToComparison,
      removeFromComparison,
      clearComparison,
    ]
  );

  return (
    <ListingsFeedContext.Provider value={value}>{children}</ListingsFeedContext.Provider>
  );
};

/**
 * Custom hook to access ListingsFeedContext.
 *
 * @returns {Object} Listings feed context value
 * @throws {Error} If used outside ListingsFeedProvider
 */
export const useListingsFeedContext = () => {
  const context = useContext(ListingsFeedContext);

  if (context === undefined) {
    throw new Error('useListingsFeedContext must be used within a ListingsFeedProvider');
  }

  return context;
};
